import angular from "angular";

import {
  ApiValidation, ApiValidationErrorType, Arrays, CellRenderingLogic, Configs, DataMapper, DataMappersLogic, EnumerationsLogic, Exportable, FileParser,
  Importable, InputEnumOption, JsonSchemaUtils, OpenAPIV3
} from "@salaxy/core";

import { UiHelpers, JsonSchemaService, FileService } from "../../services";

/** Provides Grid UI for viewing and editing arrays. */
export class SxyGridController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["JsonSchemaService", "UiHelpers", "$element", "$timeout", "FileService"];

  /** Default bindings to the components */
  public static bindings = {

    /**
     * The schema definition that defines the structure of the grid.
     */
    schemaDef: "<",

    /**
     * URL of the schema document that contains the schemaDef.
     * If set with schema in init, loads the document.
     */
    doc: "@",

    /**
     * Key of schemaDef in doc.
     * If set with doc in init, will load the schema document from server and set the schemaDef.
     */
    schema: "@",

    /** Data that is rendered / edited in the grid. */
    data: "<",

    /**
     * List of columns to include in the grid. Raw field names - may be a property tree.
     */
    include: "<",

    /**
     * List of columns to exclude from the grid.
     * This property is currenlty used only if include property is undefined, i.e. in default
     * rendering, which will render the first-level properties. As such, exclude does not really support property paths.
     */
    exclude: "<",

  };

  /**
   * URL of the schema document that contains the schemaDef.
   * If set with schema in init, loads the document.
   */
  public doc: string;

  /**
   * Key of schemaDef in doc.
   * If set with doc in init (and schemaDef not set),
   * will load the schema document from server and set the schemaDef.
   */
  public schema: string;

  /** Data that is rendered / edited in the grid. */
  public data: any[];

  /**
   * List of columns to include in the grid. Raw field names - may be a property tree.
   */
  public include: string | string[];

  /**
   * List of columns to exclude from the grid.
   * This property is currenlty used only if include property is undefined, i.e. in default
   * rendering, which will render the first-level properties. As such, exclude does not really support property paths.
   */
  public exclude: string | string[];

  /** Array of items that is selected in the grid. These are grid nodes, not data items. */
  public selection: any[] = [];

  /**
   * The grid options
   * TODO: Consider adding at least some typing.
   */
  private gridOptions: any;

  private isInitDone = false;

  _schemaDef: OpenAPIV3.NonArraySchemaObject;

  constructor(
    private jsonSchemaService: JsonSchemaService,
    private uiHelpers: UiHelpers,
    private $element: JQLite,
    private $timeout: angular.ITimeoutService,

    private fileService: FileService,
  ) {
    // Dependency injection
  }

  /** Initializes the controller */
  public $onInit() {
    this.initModel();
  }

  /**
   * The schema definition that defines the structure of each item in the grid.
   */
  public get schemaDef(): OpenAPIV3.NonArraySchemaObject {
    return this._schemaDef;
  }
  public set schemaDef(value: OpenAPIV3.NonArraySchemaObject) {
    this._schemaDef = value;
    if (this.isInitDone) {
      this.renderGrid();
    }
  }

  /**
   * Adds an empty row to the grid.
   * @param rowData Data to add to the grid. Array of of objects.
   * @param index Index position. If null, will add to the end.
   */
  public addRow(rowData: any[] = [{}], index = null): void {
    this.gridOptions.api.applyTransaction({
      add: rowData,
      addIndex: index,
    });
    this.commit();
  }

  /** Delete the selected rows from the grid. */
  public deleteSelectedRows(): void {
    const selectedRows = this.gridOptions.api.getSelectedRows();
    this.gridOptions.api.applyTransaction({ remove: selectedRows });
    this.commit();
  }

  /** Clears all rows in the grid. Sets the data to an empty array. */
  public clear(): void {
    this.gridOptions.api.setRowData([]);
    this.commit();
  }

  /** Commits the changes in the grid to the source data. */
  public commit(): void {
    this.data.length = 0;
    this.gridOptions.api.forEachNode((node) => {
      this.data.push(node.data);
    });
  }

  /**
   * Validates a single item based on the schema.
   * @param item Item to validate
   */
  public validateItem(item: any): ApiValidation {
    // TODO: Move to core JsonSchema helpers.
    if (!item) {
      return {
        isValid: false,
        hasAllRequiredFields: false,
        errors: [{
          msg: "Row is empty / null, this is not allowed: Null rows must be filtered out before adding.",
          type: ApiValidationErrorType.General,
        }],
      };
    }
    const result: ApiValidation = {
      errors: [],
    };

    Object.keys(item).forEach((key) => {
      const val = item[key];
      const prop = (this.schemaDef || {}).properties[key] as OpenAPIV3.SchemaObject;
      if (prop) {
        // Empty values are OK at least for now, required items may be added later to validation
        if (val) {
          switch (prop.type) {
            case "integer":
              if (!Number.isInteger(+val)) {
                result.errors.push({
                  msg: `Value '${val}' in ${key} is not an integer.`,
                  type: ApiValidationErrorType.Invalid,
                  key
                });
              }
              break;
            case "number":
              if (isNaN(val)) {
                result.errors.push({
                  msg: `Value '${val}' in ${key} is not a number.`,
                  type: ApiValidationErrorType.Invalid,
                  key
                });
              }
              break;
            case "boolean":
              // TODO: Perhaps add 0/1 and kyllä/ei
              if (["true", "false"].indexOf((val + "").toLowerCase()) < 0) {
                result.errors.push({
                  msg: `Value '${val}' in ${key} is not a valid boolean (true/false).`,
                  type: ApiValidationErrorType.Invalid,
                  key
                });
              }
              break;
            case "array":
              if (!Array.isArray(val)) {
                result.errors.push({
                  msg: `Value '${val}' in ${key} is not a valid array.`,
                  type: ApiValidationErrorType.Invalid,
                  key
                });
              }
              break;
          }
        }
      } else {
        result.errors.push({
          msg: `Property ${key} is not defined in the schema.`,
          type: ApiValidationErrorType.Warning,
        });
      }
    });
    (result as any).hasAllRequiredFields = !result.errors.find((x) => x.type == ApiValidationErrorType.Required);
    (result as any).isValid = !result.errors.find((x) => x.type !== ApiValidationErrorType.Warning);
    return result;
  }

  /**
   * Takes an input object with string based data and maps to correct typing or empty objects.
   * E.g. "2" => 2, "True" = true. Original data is not changed and only data in schema is returned.
   * Keep the method in sync with validateItem.
   * @param item Item to convert as typed item.
   * @returns Typed item
   */
  public convertTypes(item: any): any {
    // TODO: Move to core JsonSchema helpers.
    if (!item) {
      return null;
    }
    const result = {};
    Object.keys(item).forEach((key) => {
      const val = item[key];
      const prop = (this.schemaDef || {}).properties[key] as OpenAPIV3.SchemaObject;
      if (prop) {
        // Empty values are OK at least for now, required items may be added later to validation
        if (val) {
          switch (prop.type) {
            case "integer":
              if (Number.isInteger(+val)) {
                result[key] = Number(val);
              }
              break;
            case "number":
              if (!isNaN(val)) {
                result[key] = Number(val);
              }
              break;
            case "boolean":
              if (["true", "false"].indexOf((val + "").toLowerCase()) >= 0) {
                // TODO: Perhaps add 0/1 and kyllä/ei
                result[key] = (val + "").toLowerCase() == "true";
              }
              break;
            case "array":
              if (!Array.isArray(val)) {
                // TODO: Consider adding validation to sub-items. This would probably need to be recursive.
                result[key] = val;
              }
              break;
            case "string":
              result[key] = val;
              break;
          }
        }
      }
    });
    return result;
  }

  /** Gets the exportable for the current data model. */
  public getExportable(): Exportable<any> {

    const fullMapper = this.getMapper().reverse(this.schema + "FullExport", `Full object export for ${this.schema}`);

    const exptMapper = new DataMapper<{ [key: string]: string }, any>(this.schema + "GridExport", `Table export for ${this.schema}`);
    exptMapper.setSchema(this.schemaDef, this.schema, "source");
    exptMapper.setPathsFromSourceSchema(this.getFields());

    const mappers = [exptMapper, fullMapper];

    const result: Exportable<any> = {
      exporters: this.fileService.getDefaultExporters(),
      getExportMappers: () => {
        return mappers.map((x) => DataMappersLogic.getInfo(x));
      },
      getExportMapper: (id: string) => {
        return Promise.resolve(mappers.find((x) => x.uri == id));
      },
      list: (maxCount) => {
        const result = maxCount > 0 ? this.data.slice(0, maxCount) : this.data;
        return Promise.resolve(result);
      },
      getSingle: (item) => {
        return Promise.resolve(item);
      },
    };

    return result;

  }

  /** Gets the importable for the current data model. */
  public getImportable(): Importable<any> {
    const mapper = this.getMapper();
    return {
      getImportMappers: () => {
        const result: InputEnumOption = {
          value: null,
          text: mapper.title,
        };
        return [result];
      },
      getImportMapper() {
        return Promise.resolve(mapper);
      },
      finalize: (items) => {
        this.clear();
        this.addRow(items.map((value) => this.convertTypes(value)));
        this.commit();
        return Promise.resolve();
      },
      validate: (itemToValidate: any): Promise<{ item: any, validation: ApiValidation }> => {
        return Promise.resolve({ item: itemToValidate, validation: this.validateItem(itemToValidate) });
      },
      parsers: FileParser.getDefaultParsers(),
    };
  }

  /** Available object mapper for import based on the current schema. */
  private getMapper(): DataMapper<{ [key: string]: string }, any> {
    const result = new DataMapper<{ [key: string]: string }, any>(this.schema + "Import_sxyGrid", `Table import for ${this.schema}`);
    result.setStringToSchemaMapping(this.schemaDef, this.schema);
    return result;
  }

  private initModel() {
    this.doc = this.doc || "salaxy-secure/schemas/v02.json";
    if (!this.schemaDef && this.doc && this.schema) {
      this.jsonSchemaService.assureSchemaDocument(this.doc).then((schemaDoc) => {
        this.isInitDone = true;
        this.schemaDef = schemaDoc.components.schemas[this.schema] as OpenAPIV3.NonArraySchemaObject;
      });
    } else if (this.schemaDef) {
      this.renderGrid();
    }
  }

  /** Gets the fields based on include OR exclude and schema defs definitions. */
  private getFields(): string[] {
    let fields = Arrays.assureArray(this.include);
    if (fields.length == 0) {
      fields = Object.keys(this.schemaDef?.properties || {});
      const excludeArr = Arrays.assureArray(this.exclude);
      if (excludeArr.length > 0) {
        fields = fields.filter((x) => excludeArr.indexOf(x) < 0);
      }
    }
    return fields;
  }

  private renderGrid() {
    this.gridOptions = {
      defaultColDef: {
        editable: true
      },
      rowSelection: "multiple",
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 20,
      onSelectionChanged: (event: {
        /** Grid API */
        api: any;
        /** Column API */
        columnApi: any;
        /** Event identifier */
        type: string;
      }) => {
        this.selection = event.api.getSelectedNodes();
        this.$timeout(0);
      },
    };
    const columnDefs = [];
    this.getFields().forEach((key) => {
      const schema = JsonSchemaUtils.getProperty(this.schemaDef, key)?.schema;
      if (schema) {
        let headerName = schema.title;
        if (!headerName) {
          headerName = key.indexOf(".") ? key.substring(key.lastIndexOf(".") + 1) : key;
        }
        const colDef = {
          field: key,
          headerName,
        } as any;
        switch (schema.type) {
          case "integer":
          case "number":
            colDef.type = ["numericColumn"];
            break;
          case "object":
            colDef.cellRenderer = CellRenderingLogic.getCellRenderer(schema);
            break;
        }
        if (schema.description) {
          colDef.headerTooltip = schema.description;
        }
        if (schema.enum?.length) {
          const enumValues = EnumerationsLogic.getEnumMetadata(schema.format).values;
          colDef.cellEditor = "agSelectCellEditor";
          colDef.cellEditorParams = {
            values: enumValues.map((x) => x.name),
          };
          colDef.refData = enumValues.reduce((a, v) => ({ ...a, [v.name]: v.label }), {});
        }
        columnDefs.push(colDef);
      } else {
        console.error(`Cannot find column ${key}`);
      }
    });
    const getSalaxyGridModule = (): Promise<any> => {
      // Support both esm and umd builds...
      return Promise.resolve(Configs.global.salaxy.grid);
    };

    getSalaxyGridModule().then((salaxyGridModule) => {
      const gridWrapper = salaxyGridModule.GridWrapper;
      const agGrid = gridWrapper.agGrid;
      agGrid.LicenseManager.setLicenseKey(gridWrapper.AG_GRID_LICENCE_KEY);
      if (!agGrid.ModuleRegistry.getRegisteredModules().length) {
        agGrid.ModuleRegistry.registerModules(agGrid.AllModules);
      }

      const eGridDiv = this.$element[0].querySelector("#dataGrid");
      new agGrid.Grid(eGridDiv, this.gridOptions);
    });

    // create the grid passing in the div to use together with the columns & data we want to use
    this.$timeout(() => {
      this.gridOptions.api.setRowData(this.data);
      this.gridOptions.api.setColumnDefs(columnDefs);
    });
  }
}